/**
 * EMOTES. The one thing two players can say to each other.
 *
 * 🚨 **SIXTY OF THESE WERE DRAWN, STAGED AND MANIFESTED, AND NOTHING COULD REACH THEM.**
 * `art-src/emotes/` holds 12 expressions for each of 5 characters with a complete manifest, and the
 * web client contained not one reference to any of it. Alex, 2026-08-15: *"in the original game if
 * you clicked on the shield a number of reaper animations came up, sad, happy, angry, taunt"* and
 * *"the original feels much more interactive and fun to play."*
 *
 * ⭐ **THE ORIGINAL PUT THEM ON THE SHIELD, AND THAT IS THE RIGHT PLACE.** The hero shield is the
 * only object on the board that IS you rather than something you own, so it is where a player
 * already looks to find themselves. It also costs no new screen furniture on a board that is
 * already dense.
 *
 * ⚠️ **THE WHEEL IS DISMISSED BY ANYTHING.** It floats over a board where every card is a control,
 * and an overlay a player has to hunt for the close button on is worse than no overlay.
 */

import { DEFAULT_EMOTE_CHARACTER, EMOTES, type EmoteDef } from "../emotes.generated";
import { Api } from "../net/api";
import { audio } from "../fx/audio";

/** How long an emote stays on screen. Long enough to read, short enough not to block the board. */
const SHOW_MS = 2600;

function setFor(character?: string): readonly EmoteDef[] {
  return EMOTES[character ?? DEFAULT_EMOTE_CHARACTER] ?? EMOTES[DEFAULT_EMOTE_CHARACTER] ?? [];
}

/**
 * Show one emote over a hero's shield.
 *
 * ⚠️ **ONE AT A TIME PER SHIELD.** A player mashing the wheel would otherwise stack images on top of
 * each other, and the pile never clears because each has its own timer.
 */
export function showEmote(shield: HTMLElement, def: EmoteDef): void {
  // 🚨 **MOUNTED ABOVE THE PART OF THE BOARD THAT RE-RENDERS, NOT INSIDE IT.** The first version
  // appended to the shield itself and the emote was never seen once: the board rebuilds its subtree
  // on every server signal, and the very act of sending an emote produces one. The element was
  // created correctly and destroyed milliseconds later, so every check said "created" and the screen
  // said nothing.
  //
  // ⭐ **THE GENERAL RULE: A TRANSIENT VISUAL MUST OUTLIVE THE RENDER THAT TRIGGERED IT.** Anything
  // shown for a second or two - an emote, a floating number, a toast - cannot live inside a subtree
  // that a re-render replaces. `match.ts` keeps `el` across renders and only replaces what is inside
  // the board, so the match root is the shallowest place that survives.
  const root = shield.closest<HTMLElement>("main.match") ?? document.body;
  const key = shield.dataset["heroOwner"] ?? "self";

  // One at a time per hero: a player mashing the wheel would otherwise stack images that each clear
  // on their own timer.
  root.querySelector(`.emote[data-for="${CSS.escape(key)}"]`)?.remove();

  const img = document.createElement("img");
  img.className = "emote";
  img.dataset["for"] = key;
  img.src = def.url;
  img.alt = def.label;
  // ⚠️ Never takes a click: this sits over a hero that is a legal attack target.
  img.setAttribute("aria-hidden", "true");

  // Positioned over the shield in the ROOT's coordinate space, so a rebuild of the board beneath it
  // cannot move or destroy it.
  const a = shield.getBoundingClientRect();
  const r = root.getBoundingClientRect();
  const scale = (r.width / (root.offsetWidth || r.width)) || 1;
  // ⚠️ **IT SITS BESIDE THE SHIELD, NOT ABOVE IT.** Above put the bottom player's emote in the middle
  // of the board, over the slots they were about to play into - and the top player's would have gone
  // off the top edge entirely. Beside is the only offset that works for BOTH shields, which is what
  // a single rule has to do.
  const cx = (a.left - r.left) / scale + a.width / scale / 2;
  const cy = (a.top - r.top) / scale + a.height / scale / 2;
  const SIZE = 96;
  // To the left of the shield, clamped inside the root.
  const x = Math.max(SIZE / 2 + 6, cx - a.width / scale / 2 - SIZE / 2 - 10);
  img.style.left = `${Math.round(x)}px`;
  img.style.top = `${Math.round(cy - SIZE / 2)}px`;

  root.append(img);
  window.setTimeout(() => img.remove(), SHOW_MS);
}

/**
 * Attach the emote wheel to the player's own shield.
 *
 * ⭐ **THE EMOTE IS SHOWN LOCALLY FIRST, THEN SENT** - the same order the original uses
 * (`ProcessUnQueueAsync(new PlayEmotion(selfId, id))` and only then `PostEmotionAsync`). Waiting for
 * the round trip would put a visible delay on the one interaction whose entire purpose is to feel
 * immediate.
 */
export function attachEmoteWheel(shield: HTMLElement, character?: string): void {
  const set = setFor(character);
  if (!set.length) return;

  shield.classList.add("shield--emotable");

  let wheel: HTMLElement | null = null;
  const close = (): void => { wheel?.remove(); wheel = null; };

  const open = (): void => {
    if (wheel) { close(); return; }

    const w = document.createElement("div");
    w.className = "emotewheel";
    w.setAttribute("role", "menu");

    for (const def of set) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "emotewheel__pick";
      b.dataset["label"] = def.label;
      b.title = def.label;

      const i = document.createElement("img");
      i.src = def.url;
      i.alt = def.label;
      b.append(i);

      const t = document.createElement("span");
      t.className = "emotewheel__name";
      t.textContent = def.label;
      b.append(t);

      b.addEventListener("click", (e) => {
        e.stopPropagation();
        close();
        showEmote(shield, def);
        audio.play("click", 0.5);
        // ⚠️ Fire and forget, and a failure is SILENT BY DESIGN. The player has already seen their
        // own emote; an error toast because the opponent's socket blinked would be noise about
        // something they cannot act on.
        void Api.playEmotion(def.expr).catch(() => { /* see above */ });
      });
      w.append(b);
    }

    shield.append(w);
    wheel = w;
  };

  shield.addEventListener("click", (e) => {
    // ⚠️ Only when the shield is NOT acting as an attack target. `heroEl` renders a button in that
    // case and a click means "hit them" - opening a wheel instead would eat an attack.
    if ((e.target as HTMLElement).closest(".emotewheel")) return;
    if (shield.classList.contains("shield--target")) return;
    e.stopPropagation();
    open();
  });

  // Dismissed by anything: a click elsewhere, ESC, or the board moving on.
  document.addEventListener("click", close);
  window.addEventListener("keydown", (e) => { if (e.key === "Escape") close(); });
}

/** Look one up by its stable key, for an emote arriving from the other player. */
export function emoteByExpr(expr: string, character?: string): EmoteDef | undefined {
  return setFor(character).find((e) => e.expr === expr);
}
